import { Box, styled } from "@mui/material";

import { useIsPhonePortrait } from "contexts/panelMode";
import { ELEMENT_IDS } from "features/bookmarks/utils";

import { PROCORE_TOP_BAR, TOOLBAR_CONTAINER, TOOLBAR_Z_INDEX } from "../constants";
import { ActionButtons } from "./TopBarActionButtons";
import { SceneControls } from "./TopBarSceneControls";

/** Consistent gap between elements in portrait mode */
const PORTRAIT_GAP = 8;

/** Single composite toolbar with semi-transparent background */
const TopBarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhonePortrait",
})<{ isPhonePortrait?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    /* Portrait: use gap for consistent spacing. Normal: space-between */
    justify-content: ${({ isPhonePortrait }) => (isPhonePortrait ? "flex-start" : "space-between")};
    align-items: ${({ isPhonePortrait }) => (isPhonePortrait ? "flex-start" : "center")};
    gap: ${PORTRAIT_GAP}px;
    /* Phone portrait needs more height for vertical layout */
    min-height: ${PROCORE_TOP_BAR.HEIGHT}px;
    padding-left: max(${TOOLBAR_CONTAINER.PADDING}px, env(safe-area-inset-left, 0px));
    padding-right: max(${TOOLBAR_CONTAINER.PADDING}px, env(safe-area-inset-right, 0px));
    padding-bottom: ${TOOLBAR_CONTAINER.PADDING}px;
    padding-top: max(${TOOLBAR_CONTAINER.PADDING}px, env(safe-area-inset-top, 0px));
    background: ${PROCORE_TOP_BAR.BG};
    border-radius: 0;
    z-index: ${TOOLBAR_Z_INDEX.TOP_BAR};
    pointer-events: none;
`;

const ContentWrapper = styled(Box)`
    pointer-events: auto;
`;

/** Flex wrapper for scene controls - takes available space in portrait mode */
const SceneControlsWrapper = styled(Box)`
    pointer-events: auto;
    flex: auto;
    min-width: 0; /* Allow flex shrinking */
`;

/**
 * Mobile Top Bar
 *
 * Top bar with scene controls (left) and action buttons (right).
 * Custom implementation for complex dropdown UI requirements.
 *
 * Phone portrait: vertical name/revision layout for scene controls; action buttons always visible.
 */
export function TopBar() {
    const isPhonePortrait = useIsPhonePortrait();

    return (
        <TopBarContainer id={ELEMENT_IDS.mobileTopBar} isPhonePortrait={isPhonePortrait}>
            <SceneControlsWrapper>
                <SceneControls isPhonePortrait={isPhonePortrait} />
            </SceneControlsWrapper>
            <ContentWrapper>
                <ActionButtons />
            </ContentWrapper>
        </TopBarContainer>
    );
}
